import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MonitorPlay, Play } from "lucide-react";
import { apiFetch, getToken } from "@/api";

/* In-app YouTube player — students NEVER leave Abhyas for playback */
export const VideoPlayerModal = ({ video, onClose }) => {
  if (!video) return null;
  return (
    <div
      data-testid="video-player-modal"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        className="animate-fade-up w-full max-w-2xl overflow-hidden rounded-t-[2rem] bg-white shadow-xl sm:rounded-[2rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aspect-video w-full bg-black">
          <iframe
            data-testid="video-player-iframe"
            className="h-full w-full"
            src={`https://www.youtube.com/embed/${video.videoId}?rel=0&autoplay=1`}
            title={video.title || "Video lesson"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="p-5 sm:p-6">
          <p
            data-testid="video-player-title"
            className="font-display text-base font-extrabold leading-snug text-[#17201A]"
          >
            {video.title}
          </p>
          <p
            data-testid="video-player-channel"
            className="mt-1 text-xs font-bold text-[#647067]"
          >
            {video.channelTitle}
          </p>
          <button
            data-testid="video-player-close"
            onClick={onClose}
            className="mt-4 w-full rounded-full border border-[#E3ECE6] py-3 text-sm font-bold text-[#17201A] transition-colors duration-200 hover:border-[#16A34A] hover:text-[#16A34A]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* Fetches + renders up to 3 trusted-channel videos for a chapter.
   Requires ONLY board/class/subject/chapter — difficulty plays no part.
   layout "grid" = compact cards (Practice page aid),
   layout "rich" = full cards with description + syllabus chips + CTA
   (Learn Through Videos page). */
export const ChapterVideos = ({
  board,
  klass,
  subject,
  chapter,
  topic = null,
  layout = "grid",
  selection,
  prefix = "video",
  onWatch,
  hideLoading = false,
  onStatusChange,
}) => {
  const [state, setState] = useState({ status: "idle", videos: [] });

  // Pure observer — lets parents mirror the existing loading state visually
  useEffect(() => {
    if (onStatusChange) onStatusChange(state.status);
  }, [state.status, onStatusChange]);

  useEffect(() => {
    if (!(board && klass && subject && chapter)) {
      setState({ status: "idle", videos: [] });
      return;
    }
    if (!getToken()) {
      setState({ status: "guest", videos: [] });
      return;
    }
    let cancelled = false;
    setState({ status: "loading", videos: [] });
    const params = new URLSearchParams({
      board,
      class: String(klass),
      subject,
      chapter,
    });
    if (topic) params.set("topic", topic);
    apiFetch(`/chapter-videos?${params.toString()}`)
      .then(
        (d) => !cancelled && setState({ status: "ok", videos: d.videos || [] })
      )
      .catch(
        (e) =>
          !cancelled &&
          setState({ status: "error", videos: [], message: e.message })
      );
    return () => {
      cancelled = true;
    };
  }, [board, klass, subject, chapter, topic]);

  if (state.status === "idle")
    return (
      <p
        data-testid={`${prefix}-idle-hint`}
        className="rounded-2xl bg-[#F8FAF7] px-4 py-3.5 text-xs font-semibold text-[#647067]"
      >
        Select your board, class, subject and chapter to see video lessons.
      </p>
    );

  if (state.status === "guest")
    return (
      <p
        data-testid={`${prefix}-guest-hint`}
        className="rounded-2xl bg-[#F8FAF7] px-4 py-3.5 text-xs font-semibold text-[#647067]"
      >
        <Link to="/login" className="font-bold text-[#16A34A] hover:underline">
          Log in
        </Link>{" "}
        to unlock recommended video lessons for every chapter.
      </p>
    );

  if (state.status === "loading")
    return hideLoading ? null : (
      <p
        data-testid={`${prefix}-loading`}
        className="inline-flex items-center gap-2 rounded-2xl bg-[#F8FAF7] px-4 py-3.5 text-xs font-bold text-[#647067]"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin text-[#16A34A]" />
        Finding the best video lessons…
      </p>
    );

  if (state.status === "error")
    return (
      <p
        data-testid={`${prefix}-error`}
        className="rounded-2xl bg-[#F8FAF7] px-4 py-3.5 text-xs font-semibold text-[#647067]"
      >
        {state.message || "Video lessons are unavailable right now."}
      </p>
    );

  if (state.videos.length === 0)
    return (
      <p
        data-testid={`${prefix}-empty-state`}
        className="rounded-2xl bg-[#F8FAF7] px-4 py-3.5 text-xs font-semibold text-[#647067]"
      >
        {topic
          ? "No videos for this topic yet — try the whole chapter instead."
          : "No recommended videos for this chapter yet — check back soon."}
      </p>
    );

  if (layout === "rich")
    return (
      <div
        data-testid={`${prefix}-cards`}
        className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
      >
        {state.videos.slice(0, 3).map((v, i) => (
          <button
            key={v.videoId}
            data-testid={`${prefix}-card-${i}`}
            onClick={() => onWatch(v)}
            className="group flex flex-col overflow-hidden rounded-[1.5rem] border border-[#E3ECE6] bg-white text-left transition-[border-color,box-shadow] duration-200 hover:border-[#16A34A]/40 hover:shadow-md"
          >
            <div className="relative aspect-video w-full overflow-hidden bg-[#F8FAF7]">
              <img
                data-testid={`${prefix}-thumbnail-${i}`}
                src={v.thumbnail}
                alt={v.title}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <span className="absolute inset-0 flex items-center justify-center transition-colors duration-200 group-hover:bg-black/20">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#16A34A] text-white shadow-md transition-transform duration-200">
                  <Play className="h-5 w-5 fill-current" />
                </span>
              </span>
              {v.duration && (
                <span
                  data-testid={`${prefix}-duration-${i}`}
                  className="absolute bottom-2.5 right-2.5 rounded-md bg-black/75 px-2 py-0.5 text-[11px] font-bold tabular-nums text-white"
                >
                  {v.duration}
                </span>
              )}
            </div>
            <div className="flex flex-1 flex-col p-5">
              <p
                data-testid={`${prefix}-title-${i}`}
                className="line-clamp-2 text-sm font-bold leading-snug text-[#17201A]"
              >
                {v.title}
              </p>
              {v.description && (
                <p
                  data-testid={`${prefix}-desc-${i}`}
                  className="mt-2 line-clamp-2 text-xs font-medium leading-relaxed text-[#647067]"
                >
                  {v.description}
                </p>
              )}
              {selection && (
                <div
                  data-testid={`${prefix}-meta-${i}`}
                  className="mt-3 flex flex-wrap gap-1.5"
                >
                  {[
                    selection.board,
                    `Class ${selection.klass}`,
                    selection.subject,
                    selection.chapter,
                  ].map((c) => (
                    <span
                      key={c}
                      className="rounded-full bg-[#16A34A]/8 px-2.5 py-1 text-[10px] font-bold text-[#14532D]"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
              <span
                data-testid={`${prefix}-watch-btn-${i}`}
                className="mt-4 inline-flex items-center gap-1.5 self-start rounded-full bg-[#16A34A] px-4 py-2 text-xs font-bold text-white transition-colors duration-200 group-hover:bg-[#15803D]"
              >
                <Play className="h-3 w-3 fill-current" />
                Watch &amp; Learn
              </span>
            </div>
          </button>
        ))}
      </div>
    );

  return (
    <div data-testid={`${prefix}-cards`} className="grid gap-4 sm:grid-cols-3">
      {state.videos.slice(0, 3).map((v, i) => (
        <button
          key={v.videoId}
          data-testid={`${prefix}-card-${i}`}
          onClick={() => onWatch(v)}
          className="group overflow-hidden rounded-2xl border border-[#E3ECE6] bg-white text-left transition-[border-color,box-shadow] duration-200 hover:border-[#16A34A]/40 hover:shadow-md"
        >
          <div className="relative aspect-video w-full overflow-hidden bg-[#F8FAF7]">
            <img
              data-testid={`${prefix}-thumbnail-${i}`}
              src={v.thumbnail}
              alt={v.title}
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center transition-colors duration-200 group-hover:bg-black/20">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#16A34A] text-white shadow-md transition-transform duration-200">
                <Play className="h-5 w-5 fill-current" />
              </span>
            </span>
            {v.duration && (
              <span
                data-testid={`${prefix}-duration-${i}`}
                className="absolute bottom-2 right-2 rounded-md bg-black/75 px-2 py-0.5 text-[11px] font-bold tabular-nums text-white"
              >
                {v.duration}
              </span>
            )}
          </div>
          <div className="p-4">
            <p
              data-testid={`${prefix}-title-${i}`}
              className="line-clamp-2 text-sm font-bold leading-snug text-[#17201A]"
            >
              {v.title}
            </p>
            <div className="mt-2 flex items-center justify-between gap-2">
              <p
                data-testid={`${prefix}-channel-${i}`}
                className="truncate text-xs font-bold text-[#647067]"
              >
                {v.channelTitle}
              </p>
              <span
                data-testid={`${prefix}-watch-btn-${i}`}
                className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-[#16A34A]"
              >
                <Play className="h-3 w-3 fill-current" />
                Watch Video
              </span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

/* Practice page — compact expandable learning aid, never touches question flow */
export const UnderstandChapter = ({ board, klass, subject, chapter }) => {
  const [open, setOpen] = useState(false);
  const [watching, setWatching] = useState(null);
  if (!(board && klass && subject && chapter) || !getToken()) return null;
  return (
    <div className="mt-4">
      <button
        data-testid="understand-chapter-btn"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-full border-2 border-[#16A34A]/30 bg-white px-5 py-2.5 text-sm font-bold text-[#16A34A] transition-colors duration-200 hover:bg-[#16A34A]/5"
      >
        <MonitorPlay className="h-4 w-4" />
        Understand this chapter first
      </button>
      {open && (
        <div
          data-testid="understand-chapter-panel"
          className="animate-fade-up mt-4 rounded-[2rem] border border-[#E3ECE6] bg-white p-5 shadow-sm sm:p-6"
        >
          <ChapterVideos
            board={board}
            klass={klass}
            subject={subject}
            chapter={chapter}
            layout="grid"
            onWatch={setWatching}
          />
        </div>
      )}
      {watching && (
        <VideoPlayerModal video={watching} onClose={() => setWatching(null)} />
      )}
    </div>
  );
};
